/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isEmpty } from 'lodash';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import {
  buildCsvFromRows,
  downloadCsv,
  getInitialColumnVisibility,
  mapV1ColumnsToV2,
} from '@src/utils/tableUtils';

// Brand-primary header theme (navy bg, white text + sort icons).
// Exported so direct DataTable consumers can reuse the same look.
export const HEADER_THEME_CSS: Record<string, any> = {
  'abyss-data-table-header-container': {
    backgroundColor:
      'var(--abyss-colors-web-semantic-color-surface-container-primary) !important',
  },
  'abyss-data-table-header-container, .abyss-data-table-header-sort-arrow-icon':
    {
      color:
        'var(--abyss-colors-web-semantic-color-text-content-primaryAlt) !important',
    },
  'abyss-data-table-header-container, .abyss-data-table-header-sort-arrow-icon:hover':
    {
      color:
        'var(--abyss-colors-web-semantic-color-text-content-primaryAlt) !important',
    },
};

// Opt-in `tableCss` for sparse-column tables: `flex-grow:1` fills leftover
// width evenly. Basis stays `0` (not `auto`) so columns share space proportionally
// without the last column growing excessively.
export const FILL_WIDTH_COLUMNS_CSS: Record<string, any> = {
  'abyss-data-table-header-container': {
    flex: '1 0 0 !important',
  },
  'abyss-data-table-base-cell': {
    flex: '1 0 0 !important',
  },
};

/** Shared server-paginated table wrapper using Abyss v2 `useDataTable`. */
export const Table = ({
  columns,
  dataKey,
  fetch,
  requestParams = {},
  customHeaderSection = null,
  showGlobalFilter = true,
  showPagination = true,
  pageSizeDefault = 50,
  pageSizeOptions = [10, 25, 50, 100, 500, 1000, 5000],
  highlightRowOnHover = true,
  renderSubComponent = null,
  // Explicit height (px) for expanded sub-component rows. v2 requires this;
  // increase if nested content needs more room.
  subComponentHeight = 320,
  // Opt-in download control. 'exportVisibleData' is resolved to a custom CSV
  // export scoped to the current page's `data`; supply a function `onClick`
  // directly for other custom behavior (e.g. full dataset export).
  downloadMenuItems = null,
  // Per-table css overrides merged into DataTable.Table. Undefined by default.
  tableCss = undefined,
  // Optional callback fired whenever the table loading state changes.
  onLoadingChange = undefined,
}: any) => {
  const changeRef = useRef<any>({
    page: '',
    pageSize: '',
    globalFilter: '',
    ...requestParams,
  });
  const [trigger] = fetch();
  const router = useRouter();
  const routeCheck = router?.getLocation()?.pathname;
  const isSearchPersistedRoute =
    routeCheck === '/' || routeCheck === '/gps' || routeCheck === '/cdb';
  const [searchValue, setSearchValue] = useState(
    localStorage.getItem('globalFilter') || ''
  );

  const [data, setData] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSizeDefault,
  });
  const [globalFilter, setGlobalFilter] = useState(
    isSearchPersistedRoute ? searchValue : ''
  );

  const fetchData = useCallback(
    async (page, pageSize, _, currentGlobalFilter) => {
      changeRef.current = {
        ...changeRef?.current,
        count:
          changeRef.current?.globalFilter !== currentGlobalFilter
            ? 0
            : changeRef.current?.count,
      };
      const { data: responseData } = await trigger({
        ...changeRef?.current,
        page: page + 1 || 1,
        pageSize,
        count: changeRef?.current?.count,
        ...(currentGlobalFilter ? { search: currentGlobalFilter } : {}),
        ...requestParams,
      });

      if (
        responseData?.count ||
        responseData?.totalRecordInfo ||
        responseData?.totalRowCount
      ) {
        changeRef.current = {
          ...changeRef.current,
          count:
            responseData?.count ||
            responseData?.totalRecordInfo ||
            responseData?.totalRowCount,
        };
      }
      changeRef.current = {
        ...changeRef?.current,
        page,
        pageSize,
        globalFilter: currentGlobalFilter,
      };

      if (isSearchPersistedRoute && currentGlobalFilter !== undefined) {
        localStorage.setItem('globalFilter', currentGlobalFilter ?? '');
        setSearchValue(currentGlobalFilter);
      }

      const results = (
        responseData?.dashboardFileInfoList ||
        responseData?.dashboardList ||
        responseData?.reportData ||
        []
      ).map((x, index) => {
        return {
          ...x,
          uniqueId: x?.uniqueId ?? `${page}_${index}`,
          hideExpansion: !(x?.setIdCount > 1),
        };
      });
      return {
        results,
        count: changeRef?.current?.count || 0,
      };
    },
    [requestParams, dataKey, trigger, isSearchPersistedRoute]
  );

  // Stable ref to latest fetchData; prevents load effect re-running on renders.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  const v2Columns = useMemo(() => mapV1ColumnsToV2(columns), [columns]);
  const initialColumnVisibility = useMemo(
    () => getInitialColumnVisibility(columns),
    // Visibility is only seeded once from the initial column set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Expandable-row config. Consumer callback receives the TanStack row.
  const expandColumnConfig = useMemo(
    () =>
      typeof renderSubComponent === 'function'
        ? {
            expandMode: 'subComponent' as const,
            renderSubComponent: ({ row }: any) => renderSubComponent(row),
            subComponentHeight,
          }
        : undefined,
    [renderSubComponent, subComponentHeight]
  );

  // Resets pagination and counts when report params change.
  const resetKey = useMemo(
    () =>
      JSON.stringify({
        dataKey,
        allDate: requestParams?.allDate,
        reportType: requestParams?.reportType,
        favourites: requestParams?.favourites,
        primarySort: requestParams?.primarySort,
        secondarySort: requestParams?.secondarySort,
        userId: requestParams?.user?.userId,
      }),
    [
      dataKey,
      requestParams?.allDate,
      requestParams?.reportType,
      requestParams?.favourites,
      requestParams?.primarySort,
      requestParams?.secondarySort,
      requestParams?.user?.userId,
    ]
  );
  const prevResetKeyRef = useRef(resetKey);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (prevResetKeyRef.current !== resetKey) {
        prevResetKeyRef.current = resetKey;
        changeRef.current = {
          ...changeRef.current,
          totalError: 0,
          totalRecordInfo: 0,
          errorsShown: 0,
          count: 0,
        };
        if (pagination.pageIndex !== 0) {
          // Reset to the first page; the state change re-triggers this effect.
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          return;
        }
      }

      setIsLoading(true);
      onLoadingChange?.(true);
      try {
        const { results, count } = await fetchDataRef.current(
          pagination.pageIndex,
          pagination.pageSize,
          null,
          globalFilter
        );
        if (!cancelled) {
          setData(results);
          setRowCount(count);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, resetKey]);

  const tableState = useDataTable({
    initialData: data,
    initialColumns: v2Columns,
    rowIdKey: 'uniqueId',
    expandColumnConfig,
    // Reduced rowOverscan (default 15) limits how many off-screen rows the
    // virtualizer renders, which reduces ResizeObserver churn and the
    // flickering that occurs at non-100% zoom levels on large screens.
    // The CSS maxHeight + willChange on .abyss-data-table-scroll handles
    // the viewport bounding side of the same fix.
    virtualizationConfig: {
      rowOverscan: 10,
      columnOverscan: 2,
    },
    paginationConfig: {
      enablePagination: showPagination,
      pageSizeOptions,
    },
    tableConfig: {
      manualPagination: true,
      manualFiltering: true,
      enableColumnResizing: true,

      // Sorts the current page only; server API has no sort-direction param.
      enableSorting: true,
      rowCount,
      // Only rows with child records (`setIdCount > 1`) can expand.
      getRowCanExpand: (row: any) => !row?.original?.hideExpansion,
      // Prevent "expand all" from mounting sub-components on non-expandable
      // rows. `undefined` for expandable rows preserves default toggle state.
      getIsRowExpanded: (row: any) =>
        row?.getCanExpand?.() ? undefined : false,
      initialState: {
        columnVisibility: initialColumnVisibility,
      },
      state: {
        pagination,
        globalFilter,
      },
      onPaginationChange: (updater) => {
        setPagination((prev) =>
          typeof updater === 'function' ? updater(prev) : updater
        );
      },
      onGlobalFilterChange: (updater) => {
        const nextValue =
          typeof updater === 'function' ? updater(globalFilter) : updater;
        setGlobalFilter(nextValue ?? '');
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      },
    } as any,
  });

  // Re-sync columns when they change; prepend injected utility columns
  // (e.g. `abyss-expand`) — `setColumns` does not re-merge them.
  useEffect(() => {
    if (!isEmpty(v2Columns)) {
      const injectedColumns = (tableState.columns || []).filter(
        (column: any) =>
          typeof column?.accessorKey === 'string' &&
          column.accessorKey.startsWith('abyss-')
      );
      tableState.setColumns([...injectedColumns, ...v2Columns]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2Columns]);

  // The library's built-in 'exportVisibleData' action isn't reliably scoped to
  // just the current server-paginated page, so export directly from `data`
  // (which always holds only the current page's rows) instead.
  const resolvedDownloadMenuItems = useMemo(
    () =>
      downloadMenuItems?.map((item: any) =>
        item?.onClick === 'exportVisibleData'
          ? {
              ...item,
              onClick: () =>
                downloadCsv(
                  buildCsvFromRows(data, columns),
                  item.csvFilename || 'export.csv'
                ),
            }
          : item
      ) ?? null,
    [downloadMenuItems, data, columns]
  );

  return (
    <DataTable
      title=""
      hideHeader
      tableState={tableState}
      css={{
        'abyss-data-table-root': {
          width: '100%',
          border: 'none',
        },
      }}
    >
      <Flex
        alignItems="center"
        justify="space-between"
        css={{ padding: '8px 16px' }}
      >
        {showGlobalFilter && <DataTable.GlobalFilter />}
        {resolvedDownloadMenuItems && (
          <DataTable.DownloadDropdown
            dropdownMenuItems={resolvedDownloadMenuItems}
          />
        )}
      </Flex>
      {(customHeaderSection || showGlobalFilter || downloadMenuItems) && (
        <Flex
          alignItems="center"
          justify="space-between"
          css={{ padding: '10px' }}
        >
          <div>{customHeaderSection}</div>

          <DataTable.Pagination variant="minimal" showPageSizeDropdown={true} />
        </Flex>
      )}

      <DataTable.Table
        isLoading={isLoading}
        rowConfig={{ highlightConfig: { highlightRowOnHover } }}
        css={{
          ...HEADER_THEME_CSS,
          ...tableCss,
          // Grow columns to fill the container; basis `auto` keeps resize working.
          // Consumer `tableCss` is deep-merged so per-table overrides win.
          'abyss-data-table-base-cell': {
            flex: '1 0 0',
            fontSize: '14px',
            color: '#212223',
            fontWeight: 500,
            ...(tableCss?.['abyss-data-table-base-cell'] || {}),
          },
          // Deep-merge to preserve both the shared bg and any consumer override.
          'abyss-data-table-header-container': {
            ...HEADER_THEME_CSS['abyss-data-table-header-container'],
            flex: '1 0 0',
            fontSize: '14px',
            ...(tableCss?.['abyss-data-table-header-container'] || {}),
          },
          'abyss-data-table-header-data': {
            fontSize: '14px',
            ...(tableCss?.['abyss-data-table-header-data'] || {}),
          },
          // Bound the virtualizer viewport so it renders only visible rows.
          // `willChange: transform` promotes the container to a GPU compositing
          // layer, which stops the ResizeObserver → repaint loop that causes
          // flickering at non-100% browser zoom levels (e.g. 75% on large screens).
          'abyss-data-table-scroll': {
            maxHeight: '65vh',
            willChange: 'transform',
            border: '1px solid #cbcccd',
          },
        }}
      />
      {showPagination && (
        <div style={{ padding: '10px' }}>
          <DataTable.Pagination variant="extended" showPageSizeDropdown />
        </div>
      )}
    </DataTable>
  );
};
